/**
 * 📅 AdminDailyQuiz.js - Daily Quiz Admin Management
 * * Admin portal tab for:
 * - Viewing all questions by species/category
 * - Adding new questions
 * - Editing existing questions
 * - Managing categories
 * - Importing questions from JSON
 * - Adding references/sources
 */

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase-config'; // ✅ FIXED PATH
import DailyQuizEditor from './DailyQuizEditor';
import CategoryManager from './CategoryManager';
import './AdminDailyQuiz.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const SPECIES_OPTIONS = [
  { value: 'manatee', label: '🦭 Manatee', color: '#10B981' },
  { value: 'dolphin', label: '🐬 Dolphin', color: '#00B4D8' },
  { value: 'humpback', label: '🐋 Humpback', color: '#0C71C3' },
];

const TYPE_OPTIONS = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True/False' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: '🟢 Easy' },
  { value: 'medium', label: '🟡 Medium' },
  { value: 'hard', label: '🔴 Hard' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const AdminDailyQuiz = () => {
  // Data state
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filterSpecies, setFilterSpecies] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // View state
  const [activeTab, setActiveTab] = useState('questions'); // questions | categories | import
  const [showEditor, setShowEditor] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Modal state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importStatus, setImportStatus] = useState(null);

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  useEffect(() => {
    // Subscribe to questions
    const questionsRef = collection(db, 'dailyQuizQuestions');
    const questionsQuery = query(questionsRef, orderBy('createdAt', 'desc'));

    const unsubQuestions = onSnapshot(
      questionsQuery,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuestions(data);
        setLoading(false);
      },
      (err) => {
        console.error('Questions load error:', err);
        setError('Failed to load questions');
        setLoading(false);
      }
    );

    // Subscribe to categories
    const catsRef = collection(db, 'quizCategories');
    const catsQuery = query(catsRef, orderBy('name'));

    const unsubCats = onSnapshot(catsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(data);
    });

    return () => {
      unsubQuestions();
      unsubCats();
    };
  }, []);

  // =========================================================================
  // FILTERING
  // =========================================================================

  const filteredQuestions = questions.filter((q) => {
    if (filterSpecies !== 'all' && q.species !== filterSpecies) return false;
    if (filterCategory !== 'all' && q.categoryId !== filterCategory) return false;
    if (filterType !== 'all' && q.questionType !== filterType) return false;
    if (filterActive === 'active' && !q.isActive) return false;
    if (filterActive === 'inactive' && q.isActive) return false;
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesQuestion = q.question?.toLowerCase().includes(search);
      const matchesCategory = q.category?.toLowerCase().includes(search);
      if (!matchesQuestion && !matchesCategory) return false;
    }
    return true;
  });

  // Filter categories by selected species
  const speciesCategories = filterSpecies === 'all'
    ? categories
    : categories.filter((c) => c.species === filterSpecies);

  // Stats
  const stats = {
    total: questions.length,
    active: questions.filter((q) => q.isActive).length,
    bySpecies: {
      manatee: questions.filter((q) => q.species === 'manatee').length,
      dolphin: questions.filter((q) => q.species === 'dolphin').length,
      humpback: questions.filter((q) => q.species === 'humpback').length,
    },
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleCreate = () => {
    setEditingQuestion(null);
    setShowEditor(true);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingQuestion(null);
  };

  const handleToggleActive = async (question) => {
    try {
      await updateDoc(doc(db, 'dailyQuizQuestions', question.id), {
        isActive: !question.isActive,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Toggle error:', err);
      alert('Failed to update: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await deleteDoc(doc(db, 'dailyQuizQuestions', deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  // =========================================================================
  // IMPORT
  // =========================================================================

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (Array.isArray(data)) {
          setImportData(data);
          setImportStatus(null);
        } else {
          alert('Invalid format: Expected an array of questions');
        }
      } catch (err) {
        alert('Invalid JSON file: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importData || importData.length === 0) return;

    setImportStatus({ status: 'importing', progress: 0 });

    try {
      // Import in batches of 500
      const batchSize = 500;
      let imported = 0;

      for (let i = 0; i < importData.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = importData.slice(i, i + batchSize);

        for (const question of chunk) {
          const ref = doc(collection(db, 'dailyQuizQuestions'), question.id || undefined);
          batch.set(ref, {
            ...question,
            id: ref.id,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }

        await batch.commit();
        imported += chunk.length;
        setImportStatus({ 
          status: 'importing', 
          progress: Math.round((imported / importData.length) * 100) 
        });
      }

      setImportStatus({ status: 'success', count: imported });
      setImportData(null);
    } catch (err) {
      console.error('Import error:', err);
      setImportStatus({ status: 'error', message: err.message });
    }
  };

  // =========================================================================
  // RENDER: STATS
  // =========================================================================

  const renderStats = () => (
    <div className="adq-stats">
      <div className="adq-stat-card">
        <span className="adq-stat-value">{stats.total}</span>
        <span className="adq-stat-label">Total Questions</span>
      </div>
      <div className="adq-stat-card">
        <span className="adq-stat-value">{stats.active}</span>
        <span className="adq-stat-label">Active</span>
      </div>
      <div className="adq-stat-card manatee">
        <span className="adq-stat-value">{stats.bySpecies.manatee}</span>
        <span className="adq-stat-label">🦭 Manatee</span>
      </div>
      <div className="adq-stat-card dolphin">
        <span className="adq-stat-value">{stats.bySpecies.dolphin}</span>
        <span className="adq-stat-label">🐬 Dolphin</span>
      </div>
      <div className="adq-stat-card humpback">
        <span className="adq-stat-value">{stats.bySpecies.humpback}</span>
        <span className="adq-stat-label">🐋 Humpback</span>
      </div>
    </div>
  );

  // =========================================================================
  // RENDER: FILTERS
  // =========================================================================

  const renderFilters = () => (
    <div className="adq-filters">
      <div className="adq-filter-group">
        <label>Species</label>
        <select value={filterSpecies} onChange={(e) => {
          setFilterSpecies(e.target.value);
          setFilterCategory('all');
        }}>
          <option value="all">All Species</option>
          {SPECIES_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="adq-filter-group">
        <label>Category</label>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="all">All Categories</option>
          {speciesCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      <div className="adq-filter-group">
        <label>Type</label>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="adq-filter-group">
        <label>Status</label>
        <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="adq-filter-group adq-search">
        <label>Search</label>
        <input
          type="text"
          placeholder="Search questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
    </div>
  );

  // =========================================================================
  // RENDER: QUESTIONS TABLE
  // =========================================================================

  const renderQuestionsTable = () => (
    <div className="adq-table-wrapper">
      <table className="adq-table">
        <thead>
          <tr>
            <th style={{ width: 100 }}>Species</th>
            <th style={{ width: 150 }}>Category</th>
            <th>Question</th>
            <th style={{ width: 70 }}>Type</th>
            <th style={{ width: 90 }}>Stats</th>
            <th style={{ width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredQuestions.map((q) => {
            const speciesOpt = SPECIES_OPTIONS.find((s) => s.value === q.species);
            const accuracy = q.timesShown > 0 
              ? Math.round((q.timesCorrect / q.timesShown) * 100) 
              : 0;

            return (
              <tr key={q.id} className={!q.isActive ? 'inactive' : ''}>
                <td>
                  <span 
                    className="adq-species-badge"
                    style={{ backgroundColor: speciesOpt?.color || '#666' }}
                  >
                    {speciesOpt?.label || q.species}
                  </span>
                </td>
                <td className="adq-category-cell">{q.category}</td>
                <td className="adq-question-cell">
                  <div className="adq-question-text">
                    {q.question?.substring(0, 100)}
                    {q.question?.length > 100 && '...'}
                  </div>
                  {q.imageUrl && <span className="adq-has-image" title="Has image">🖼️</span>}
                </td>
                <td className="adq-type-cell">
                  {q.questionType === 'true_false' ? 'T/F' : 'MC'}
                </td>
                <td className="adq-stats-cell">
                  <span title={`${q.timesShown || 0} shown, ${q.timesCorrect || 0} correct`}>
                    {q.timesShown || 0} / {accuracy}%
                  </span>
                </td>
                <td className="adq-actions-cell">
                  <button
                    className={`adq-btn-icon ${q.isActive ? 'active' : ''}`}
                    onClick={() => handleToggleActive(q)}
                    title={q.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {q.isActive ? '✅' : '⏸️'}
                  </button>
                  <button
                    className="adq-btn-icon"
                    onClick={() => handleEdit(q)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="adq-btn-icon danger"
                    onClick={() => setDeleteConfirm(q)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredQuestions.length === 0 && (
        <div className="adq-empty">
          <p>No questions found matching your filters.</p>
        </div>
      )}
    </div>
  );

  // =========================================================================
  // RENDER: IMPORT TAB
  // =========================================================================

  const renderImportTab = () => (
    <div className="adq-import">
      <div className="adq-import-header">
        <h3>📥 Import Questions</h3>
        <p>Import questions from a JSON file (array of question objects)</p>
      </div>

      <div className="adq-import-upload">
        <input
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          id="import-file-input"
        />
        <label htmlFor="import-file-input" className="adq-btn adq-btn-secondary">
          📁 Select JSON File
        </label>
      </div>

      {importData && (
        <div className="adq-import-preview">
          <h4>Preview: {importData.length} questions</h4>
          <div className="adq-import-list">
            {importData.slice(0, 5).map((q, i) => (
              <div key={i} className="adq-import-item">
                <span className="adq-import-species">{q.species}</span>
                <span className="adq-import-question">
                  {q.question?.substring(0, 80)}...
                </span>
              </div>
            ))}
            {importData.length > 5 && (
              <p className="adq-import-more">...and {importData.length - 5} more</p>
            )}
          </div>
          <div className="adq-import-actions">
            <button 
              className="adq-btn adq-btn-secondary"
              onClick={() => setImportData(null)}
            >
              Cancel
            </button>
            <button 
              className="adq-btn adq-btn-success"
              onClick={handleImport}
              disabled={importStatus?.status === 'importing'}
            >
              {importStatus?.status === 'importing' 
                ? `Importing... ${importStatus.progress}%`
                : `Import ${importData.length} Questions`
              }
            </button>
          </div>
        </div>
      )}

      {importStatus?.status === 'success' && (
        <div className="adq-import-success">
          ✅ Successfully imported {importStatus.count} questions!
        </div>
      )}

      {importStatus?.status === 'error' && (
        <div className="adq-import-error">
          ❌ Import failed: {importStatus.message}
        </div>
      )}

      <div className="adq-import-format">
        <h4>Expected JSON Format:</h4>
        <pre>{`[
  {
    "id": "dq_001",
    "species": "manatee",
    "category": "Manatee Ecology",
    "categoryId": "manatee_ecology",
    "question": "Question text here?",
    "questionType": "multiple_choice",
    "imageUrl": "https://...",
    "options": [
      { "id": "a", "text": "Answer A", "isCorrect": false },
      { "id": "b", "text": "Answer B", "isCorrect": true }
    ],
    "correctAnswerId": "b",
    "explanation": "Explanation text...",
    "difficulty": "medium",
    "isActive": true
  }
]`}</pre>
      </div>
    </div>
  );

  // =========================================================================
  // RENDER: DELETE MODAL
  // =========================================================================

  const renderDeleteModal = () => {
    if (!deleteConfirm) return null;

    return (
      <div className="adq-modal-overlay" onClick={() => setDeleteConfirm(null)}>
        <div className="adq-modal" onClick={(e) => e.stopPropagation()}>
          <h3>⚠️ Delete Question</h3>
          <p>Are you sure you want to delete this question?</p>
          <div className="adq-modal-preview">
            "{deleteConfirm.question?.substring(0, 100)}..."
          </div>
          <div className="adq-modal-actions">
            <button 
              className="adq-btn adq-btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button 
              className="adq-btn adq-btn-danger"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  // Show editor if editing
  if (showEditor) {
    return (
      <DailyQuizEditor
        question={editingQuestion}
        categories={categories}
        onClose={handleEditorClose}
        onSave={handleEditorClose}
      />
    );
  }

  // Show category manager
  if (showCategoryManager) {
    return (
      <CategoryManager
        categories={categories}
        onClose={() => setShowCategoryManager(false)}
      />
    );
  }

  return (
    <div className="adq-container">
      {/* Header */}
      <div className="adq-header">
        <div className="adq-header-left">
          <h2>📅 Daily Quiz Management</h2>
          <p>Manage educational Q&A questions • Users never see the same question twice</p>
        </div>
        <div className="adq-header-actions">
          <button
            className="adq-btn adq-btn-secondary"
            onClick={() => setShowCategoryManager(true)}
          >
            📁 Categories
          </button>
          <button className="adq-btn adq-btn-primary" onClick={handleCreate}>
            ➕ Add Question
          </button>
        </div>
      </div>

      {/* Stats */}
      {renderStats()}

      {/* Tabs */}
      <div className="adq-tabs">
        <button
          className={`adq-tab ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          📝 Questions ({filteredQuestions.length})
        </button>
        <button
          className={`adq-tab ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          📥 Import
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="adq-loading">
          <div className="adq-spinner" />
          <p>Loading questions...</p>
        </div>
      ) : error ? (
        <div className="adq-error">
          <p>❌ {error}</p>
        </div>
      ) : activeTab === 'questions' ? (
        <>
          {renderFilters()}
          {renderQuestionsTable()}
        </>
      ) : (
        renderImportTab()
      )}

      {/* Delete Modal */}
      {renderDeleteModal()}
    </div>
  );
};

export default AdminDailyQuiz;